import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const statusColors = {
  todo: "#0055FF", in_progress: "#D97706",
  review: "#7C3AED", done: "#16A34A",
};

export default function GanttChart({ tasks, onTaskClick, onTaskUpdate, user }) {
  const [dragInfo, setDragInfo] = useState(null);
  const [dragDelta, setDragDelta] = useState(0);
  const [showCriticalPath, setShowCriticalPath] = useState(true);
  const containerRef = useRef(null);

  const { allTasks, sortedTasks, minDate, totalDays, weeks, taskMap } = useMemo(() => {
    const withDates = tasks.filter(t => t.start_date && t.end_date);
    const withoutDates = tasks.filter(t => !t.start_date || !t.end_date);
    // All tasks appear in the left column: dated first, then undated
    const all = [...withDates.sort((a, b) => a.start_date.localeCompare(b.start_date)), ...withoutDates];

    if (withDates.length === 0) {
      const map = {};
      all.forEach((t, i) => { map[t.id] = { ...t, rowIndex: i }; });
      return { allTasks: all, sortedTasks: [], minDate: null, totalDays: 0, weeks: [], taskMap: map };
    }
    const dates = withDates.flatMap(t => [new Date(t.start_date), new Date(t.end_date)]);
    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));
    min.setDate(min.getDate() - 7);
    max.setDate(max.getDate() + 14);
    const total = Math.ceil((max - min) / (1000 * 60 * 60 * 24));
    const wks = [];
    const curr = new Date(min);
    curr.setDate(curr.getDate() - curr.getDay());
    while (curr <= max) { wks.push(new Date(curr)); curr.setDate(curr.getDate() + 7); }
    const map = {};
    all.forEach((t, i) => { map[t.id] = { ...t, rowIndex: i }; });
    return { allTasks: all, sortedTasks: withDates, minDate: min, totalDays: total, weeks: wks, taskMap: map };
  }, [tasks]);

  const dayWidth = 32;
  const chartWidth = totalDays * dayWidth;
  const rowHeight = 44;
  const headerHeight = 50;
  const labelWidth = 220;
  const isInternal = user?.user_type === "internal";

  const getOffset = useCallback((dateStr) => {
    return Math.round((new Date(dateStr) - minDate) / (1000 * 60 * 60 * 24)) * dayWidth;
  }, [minDate, dayWidth]);

  const getWidth = useCallback((start, end) => {
    return Math.max(Math.round((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24)) * dayWidth, dayWidth);
  }, [dayWidth]);

  const addDays = (dateStr, days) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d.toISOString().split("T")[0];
  };

  // Critical path computation
  const criticalPath = useMemo(() => {
    if (sortedTasks.length === 0) return new Set();

    // Build successor map
    const successors = {};
    sortedTasks.forEach(t => {
      (t.dependencies || []).forEach(depId => {
        if (!successors[depId]) successors[depId] = [];
        successors[depId].push(t.id);
      });
    });

    // Forward pass: compute earliest finish
    const ef = {};
    const taskDur = {};
    sortedTasks.forEach(t => {
      const s = new Date(t.start_date), e = new Date(t.end_date);
      taskDur[t.id] = Math.round((e - s) / 86400000);
      let es = s;
      (t.dependencies || []).forEach(depId => {
        const dep = taskMap[depId];
        if (dep) {
          const depEnd = new Date(dep.end_date);
          depEnd.setDate(depEnd.getDate() + 1);
          if (depEnd > es) es = depEnd;
        }
      });
      const efDate = new Date(es);
      efDate.setDate(efDate.getDate() + taskDur[t.id]);
      ef[t.id] = efDate;
    });

    // Project end
    let projectEnd = new Date(0);
    sortedTasks.forEach(t => { if (ef[t.id] > projectEnd) projectEnd = new Date(ef[t.id]); });

    // Backward pass: compute latest finish
    const lf = {};
    [...sortedTasks].reverse().forEach(t => {
      const succs = successors[t.id] || [];
      if (succs.length === 0) {
        lf[t.id] = new Date(projectEnd);
      } else {
        let earliest = new Date(projectEnd);
        succs.forEach(succId => {
          const succLf = lf[succId] || new Date(projectEnd);
          const succLs = new Date(succLf);
          succLs.setDate(succLs.getDate() - (taskDur[succId] || 0));
          if (succLs < earliest) earliest = new Date(succLs);
        });
        lf[t.id] = earliest;
      }
    });

    // Float = LF - EF; critical if float <= 1 day
    const critical = new Set();
    sortedTasks.forEach(t => {
      const float = Math.round((lf[t.id] - ef[t.id]) / 86400000);
      if (float <= 1) critical.add(t.id);
    });
    return critical;
  }, [sortedTasks, taskMap]);

  // Dependency lines
  const dependencyLines = useMemo(() => {
    const lines = [];
    allTasks.forEach(task => {
      if (!task.dependencies || task.dependencies.length === 0) return;
      if (!task.start_date || !task.end_date) return;
      task.dependencies.forEach(depId => {
        const depTask = taskMap[depId];
        if (!depTask || !depTask.end_date) return;
        const fromLeft = getOffset(depTask.end_date);
        const fromY = depTask.rowIndex * rowHeight + rowHeight / 2;
        const toLeft = getOffset(task.start_date);
        const toY = taskMap[task.id].rowIndex * rowHeight + rowHeight / 2;
        const isCritical = showCriticalPath && criticalPath.has(depId) && criticalPath.has(task.id);
        lines.push({ fromX: fromLeft, fromY, toX: toLeft, toY, isCritical });
      });
    });
    return lines;
  }, [allTasks, taskMap, getOffset, rowHeight, criticalPath, showCriticalPath]);

  // Drag handlers
  const handleBarMouseDown = (e, task, type) => {
    if (!isInternal) return;
    e.preventDefault();
    e.stopPropagation();
    setDragInfo({ taskId: task.id, type, startX: e.clientX, task });
    setDragDelta(0);
  };

  useEffect(() => {
    if (!dragInfo) return;
    const handleMouseMove = (e) => setDragDelta(e.clientX - dragInfo.startX);
    const handleMouseUp = async (e) => {
      const delta = e.clientX - dragInfo.startX;
      const deltaDays = Math.round(delta / dayWidth);
      if (deltaDays === 0) { setDragInfo(null); setDragDelta(0); return; }
      const task = dragInfo.task;
      let newStart = task.start_date, newEnd = task.end_date;
      if (dragInfo.type === "move") {
        newStart = addDays(task.start_date, deltaDays);
        newEnd = addDays(task.end_date, deltaDays);
      } else if (dragInfo.type === "resize-left") {
        newStart = addDays(task.start_date, deltaDays);
        if (new Date(newStart) >= new Date(newEnd)) newStart = addDays(newEnd, -1);
      } else if (dragInfo.type === "resize-right") {
        newEnd = addDays(task.end_date, deltaDays);
        if (new Date(newEnd) <= new Date(newStart)) newEnd = addDays(newStart, 1);
      }
      try {
        const res = await api.put(`/tasks/${task.id}`, { start_date: newStart, end_date: newEnd });
        const cascaded = res.data?.cascaded_updates || [];
        if (cascaded.length > 0) {
          toast.success(`Task updated. ${cascaded.length} downstream task${cascaded.length > 1 ? "s" : ""} rescheduled automatically.`);
        } else {
          toast.success("Task dates updated");
        }
        onTaskUpdate?.();
      } catch { toast.error("Failed to update dates"); }
      setDragInfo(null);
      setDragDelta(0);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => { document.removeEventListener("mousemove", handleMouseMove); document.removeEventListener("mouseup", handleMouseUp); };
  }, [dragInfo, dayWidth, onTaskUpdate]);

  if (allTasks.length === 0) {
    return (
      <div className="text-center py-16 text-[#A3A3A3]" data-testid="gantt-empty">
        <p className="font-medium">No tasks to display</p>
        <p className="text-xs mt-1">Add tasks to see the Gantt chart</p>
      </div>
    );
  }

  const todayOffset = minDate ? getOffset(new Date().toISOString().split("T")[0]) : 0;
  const formatWeek = (date) => date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const chartHeight = allTasks.length * rowHeight;

  return (
    <div className="border border-[#E5E5E5]" data-testid="gantt-chart" ref={containerRef}>
      {/* Header Bar */}
      <div className="bg-[#F5F5F5] border-b border-[#E5E5E5] px-3 py-1.5 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          {isInternal && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#0055FF]">
              Drag bars to reschedule
            </span>
          )}
          {dependencyLines.length > 0 && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
              {dependencyLines.length} dependencies
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Critical Path Toggle */}
          <button
            onClick={() => setShowCriticalPath(!showCriticalPath)}
            className={`flex items-center gap-1.5 px-2 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${
              showCriticalPath ? "bg-[#DC2626]/10 text-[#DC2626]" : "text-[#A3A3A3] hover:text-[#525252]"
            }`}
            data-testid="toggle-critical-path"
          >
            <div className={`w-3 h-1.5 ${showCriticalPath ? "bg-[#DC2626]" : "bg-[#A3A3A3]"}`} />
            Critical Path ({criticalPath.size})
          </button>
        </div>
      </div>

      <div className="overflow-x-auto gantt-container" style={{ maxHeight: "70vh" }}>
        <div className="flex" style={{ minWidth: labelWidth + chartWidth }}>
          {/* Task Labels */}
          <div className="flex-shrink-0 border-r border-[#E5E5E5] bg-white sticky left-0 z-10" style={{ width: labelWidth }}>
            <div className="h-[50px] border-b border-[#E5E5E5] flex items-center px-3">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">Task</span>
            </div>
            {allTasks.map((task) => {
              const depCount = (task.dependencies || []).length;
              const isCritical = showCriticalPath && criticalPath.has(task.id);
              const hasDates = task.start_date && task.end_date;
              return (
                <div
                  key={task.id}
                  className={`flex items-center px-3 border-b cursor-pointer transition-colors ${
                    task.ball_with === "Client"
                      ? "bg-[#FEE2E2] hover:bg-[#FECACA]"
                      : "hover:bg-[#F5F5F5]"
                  } ${
                    isCritical ? "border-l-2 border-l-[#DC2626] border-b-[#E5E5E5]" : "border-b-[#E5E5E5]"
                  }`}
                  style={{ height: rowHeight }}
                  onClick={() => onTaskClick?.(task)}
                  onContextMenu={(e) => { e.preventDefault(); onTaskClick?.(task); }}
                  data-testid={`gantt-task-label-${task.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium truncate ${isCritical ? "text-[#DC2626]" : hasDates ? "text-[#0A0A0A]" : "text-[#A3A3A3]"}`}>{task.title}</p>
                    <div className="flex items-center gap-2">
                      {hasDates && task.duration_days && <span className="text-[10px] text-[#525252] font-medium">{task.duration_days}d</span>}
                      {!hasDates && <span className="text-[10px] text-[#D97706] font-medium italic">No dates</span>}
                      {depCount > 0 && (
                        <span className="text-[10px] text-[#0055FF] font-medium">{depCount} dep{depCount > 1 ? "s" : ""}</span>
                      )}
                      {isCritical && (
                        <Badge className="bg-[#DC2626]/10 text-[#DC2626] border-0 text-[8px] font-bold px-1 py-0 rounded-none">
                          CRITICAL
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Chart Area */}
          <div className="flex-1 relative" style={{ width: chartWidth }}>
            {/* Week Headers */}
            <div className="flex border-b border-[#E5E5E5] relative" style={{ height: headerHeight }}>
              {weeks.map((week, i) => {
                const offset = getOffset(week.toISOString().split("T")[0]);
                return (
                  <div key={i} className="absolute text-[10px] font-medium text-[#A3A3A3] border-l border-[#F5F5F5] flex items-end pb-2 pl-2" style={{ left: offset, width: dayWidth * 7, height: headerHeight }}>
                    {formatWeek(week)}
                  </div>
                );
              })}
            </div>

            {/* Bars + Dependencies + Critical Path */}
            <div className="relative" style={{ userSelect: dragInfo ? "none" : "auto" }}>
              {/* Grid lines */}
              {weeks.map((week, i) => {
                const offset = getOffset(week.toISOString().split("T")[0]);
                return <div key={`grid-${i}`} className="absolute top-0 border-l border-[#F5F5F5]" style={{ left: offset, height: chartHeight }} />;
              })}

              {/* Today line */}
              {todayOffset > 0 && todayOffset < chartWidth && (
                <div className="absolute top-0 border-l-2 border-[#DC2626] z-10" style={{ left: todayOffset, height: chartHeight }}>
                  <div className="bg-[#DC2626] text-white text-[9px] font-bold px-1 py-0.5 -ml-3">TODAY</div>
                </div>
              )}

              {/* Dependency SVG Lines */}
              {dependencyLines.length > 0 && (
                <svg className="absolute top-0 left-0 pointer-events-none z-[5]" width={chartWidth} height={chartHeight} style={{ overflow: "visible" }}>
                  {dependencyLines.map((line, i) => {
                    const midX = line.fromX + 12;
                    const path = line.fromY === line.toY
                      ? `M ${line.fromX} ${line.fromY} L ${line.toX} ${line.toY}`
                      : `M ${line.fromX} ${line.fromY} H ${midX} V ${line.toY} H ${line.toX}`;
                    const color = line.isCritical ? "#DC2626" : "#0A0A0A";
                    const opacity = line.isCritical ? 0.7 : 0.3;
                    const width = line.isCritical ? 2 : 1.5;
                    return (
                      <g key={i}>
                        <path d={path} fill="none" stroke={color} strokeWidth={width} opacity={opacity} />
                        <polygon
                          points={`${line.toX},${line.toY} ${line.toX - 6},${line.toY - 4} ${line.toX - 6},${line.toY + 4}`}
                          fill={color} opacity={opacity}
                        />
                        <circle cx={line.fromX} cy={line.fromY} r="3" fill={color} opacity={opacity} />
                      </g>
                    );
                  })}
                </svg>
              )}

              {/* Task Bars */}
              {allTasks.map((task) => {
                const hasDates = task.start_date && task.end_date;
                if (!hasDates) {
                  return <div key={task.id} className="border-b border-[#E5E5E5] relative" style={{ height: rowHeight }} onContextMenu={(e) => { e.preventDefault(); onTaskClick?.(task); }} />;
                }
                let left = getOffset(task.start_date);
                let width = getWidth(task.start_date, task.end_date);
                const color = statusColors[task.status] || "#A3A3A3";
                const isDragging = dragInfo?.taskId === task.id;
                const isCritical = showCriticalPath && criticalPath.has(task.id);

                if (isDragging) {
                  if (dragInfo.type === "move") { left += dragDelta; }
                  else if (dragInfo.type === "resize-left") { left += dragDelta; width -= dragDelta; if (width < dayWidth) width = dayWidth; }
                  else if (dragInfo.type === "resize-right") { width += dragDelta; if (width < dayWidth) width = dayWidth; }
                }

                return (
                  <div key={task.id} className="border-b border-[#E5E5E5] relative" style={{ height: rowHeight }}>
                    <div
                      className={`gantt-bar group ${isDragging ? "opacity-80 shadow-lg" : ""}`}
                      style={{
                        left, width: Math.max(width, dayWidth),
                        top: (rowHeight - 24) / 2,
                        backgroundColor: color,
                        cursor: isInternal ? "grab" : "pointer",
                        outline: isCritical ? "2px solid #DC2626" : "none",
                        outlineOffset: "1px",
                        boxShadow: isCritical ? "0 0 8px rgba(220,38,38,0.3)" : undefined,
                      }}
                      onClick={() => { if (!dragInfo) onTaskClick?.(task); }}
                      onContextMenu={(e) => { e.preventDefault(); onTaskClick?.(task); }}
                      data-testid={`gantt-bar-${task.id}`}
                    >
                      <span className="relative z-10 text-white pointer-events-none">{task.title}</span>
                      {isInternal && (
                        <>
                          <div className="absolute left-0 top-0 bottom-0 w-[6px] cursor-ew-resize opacity-0 group-hover:opacity-100 bg-white/30 z-20" onMouseDown={(e) => handleBarMouseDown(e, task, "resize-left")} />
                          <div className="absolute right-0 top-0 bottom-0 w-[6px] cursor-ew-resize opacity-0 group-hover:opacity-100 bg-white/30 z-20" onMouseDown={(e) => handleBarMouseDown(e, task, "resize-right")} />
                          <div className="absolute left-[6px] right-[6px] top-0 bottom-0 cursor-grab z-10" onMouseDown={(e) => handleBarMouseDown(e, task, "move")} />
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
